import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Settings, LogOut, Flame, Trophy, Bookmark, Heart,
  BookOpen, Calendar, ChevronRight, Edit3, Check, X,
  Brain, Moon, Wind, Sparkles, Zap, Compass, Sun, MoonStar,
  GraduationCap, Award, BookHeart, Lock, Clock
} from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { formatMindfulTime } from '../utils/mindful';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import FriendsSection from '../components/FriendsSection';
import PushNotificationSettings from '../components/PushNotificationSettings';
import PushOptInCard from '../components/PushOptInCard';
import ReflectionReminderSettings from '../components/ReflectionReminderSettings';
import { MySharedLinks } from '../components/programs/MySharedLinks';
import ReflectionCoachOverlay from '../components/ReflectionCoachOverlay';
import DangerZone from '../components/DangerZone';
import MilestoneBadges from '../components/MilestoneBadges';
import { MessageCircle, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Flame, BookOpen, GraduationCap, Brain, Moon, Wind, Zap, Trophy,
  Heart, Bookmark, Compass, Sparkles, Sun, MoonStar,
};

const ModeratorPanel = () => {
  // Small tile that shows the pending report count and links to the queue.
  // Only rendered when user.isAdmin === true (gated in ProfilePage below).
  const [count, setCount] = useState(null);
  useEffect(() => {
    const t = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/admin/reports/pending-count`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then((r) => (r.ok ? r.json() : { pendingCount: 0 }))
      .then((b) => setCount(b.pendingCount || 0))
      .catch(() => setCount(0));
  }, []);
  return (
    <div className="px-5 mb-6" data-testid="profile-moderator-panel">
      <Link
        to="/moderation"
        className="flex items-center gap-3 p-4 rounded-2xl bg-[#FFFCF3] border border-orange-200 hover:shadow-md transition-shadow active:scale-[0.99]"
        data-testid="profile-moderation-link"
      >
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ background: 'linear-gradient(135deg, #E76F51 0%, #B45539 100%)' }}>
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.24em] font-semibold text-orange-500 mb-0.5">
            Moderator
          </p>
          <p className="text-[14px] font-semibold text-stone-800">Report Queue</p>
          <p className="text-[11px] text-stone-500 mt-0.5">
            {count === null
              ? 'loading…'
              : count === 0
                ? 'Inbox zero. Nicely done. 🌱'
                : `${count} pending ${count === 1 ? 'report' : 'reports'} to review`}
          </p>
        </div>
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[10px] font-bold text-white bg-orange-500">
            {count}
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
      </Link>
    </div>
  );
};


const ProfilePage = () => {
  const navigate = useNavigate();
  const {
    user, streak, badges, readPosts, likedPosts, bookmarkedPosts,
    earnedBadgeCount, totalBadgeCount, logout, updateProfile,
    updateProfilePicture, refreshProfile, isLoading,
  } = useAuth();
  const { getPostById, getProgramById } = useContent();
  const [activeTab, setActiveTab] = useState('badges');
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [inProgress, setInProgress] = useState([]);
  const [coachOpen, setCoachOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/programs/in-progress`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setInProgress(Array.isArray(rows) ? rows : []))
      .catch(() => setInProgress([]));
  }, [user]);

  // Redirect to /auth if not authenticated. Must be in an effect, NOT during render —
  // calling navigate() during render triggers a React "setState during render" warning
  // because it tries to update the parent Router while ProfilePage is rendering.
  useEffect(() => {
    if (!isLoading && !user) navigate('/auth', { replace: true });
  }, [user, isLoading, navigate]);

  if (!user) {
    return isLoading ? <div className="px-5 pt-10" data-testid="profile-loading"><div className="h-40 rounded-3xl bg-white/10 animate-pulse" /></div> : null;
  }

  const mindfulSeconds = user.mindfulSeconds || 0;

  const handleEditName = () => {
    setNewName(user.name);
    setEditingName(true);
  };

  const saveName = () => {
    if (newName.trim()) {
      updateProfile({ name: newName.trim() });
    }
    setEditingName(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Generate streak calendar (last 30 days)
  const generateCalendar = () => {
    const days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        date: dateStr,
        active: streak.history?.includes(dateStr) || false,
        isToday: i === 0,
        dayLabel: date.toLocaleDateString('en', { weekday: 'narrow' }),
        dayNum: date.getDate(),
      });
    }
    return days;
  };

  const calendarDays = generateCalendar();
  const earnedBadges = badges.filter((b) => b.earned);
  const lockedBadges = badges.filter((b) => !b.earned);

  return (
    <div className="animate-fade-in pb-4">
      {/* Header */}
      <div className="px-5 pt-4 flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-stone-500 hover:text-[#D6AD60] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Home</span>
        </button>
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="flex items-center gap-1.5 text-stone-500 hover:text-red-400 transition-colors duration-300"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-xs">Log out</span>
        </button>
      </div>

      {/* Profile Card */}
      <div className="px-5 mb-6">
        <div className="bg-[#FFFCF3] border border-stone-200 rounded-3xl p-5 shadow-md">
          <div className="flex items-center gap-4">
            <ProfilePictureUpload
              currentPicture={user.profilePicture || user.avatar}
              onUpload={(base64) => updateProfilePicture(base64)}
            />
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="text-lg font-semibold text-stone-800 bg-[#E8DDCB] rounded-xl px-3 py-1 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/40 w-full"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    autoFocus
                  />
                  <button onClick={saveName} className="text-green-400 hover:text-green-300">
                    <Check className="w-5 h-5" />
                  </button>
                  <button onClick={() => setEditingName(false)} className="text-stone-500 hover:text-[#D6AD60]">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2
                    className="text-lg font-semibold text-stone-800 truncate"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {user.name}
                  </h2>
                  <button onClick={handleEditName} className="text-stone-500 hover:text-[#D6AD60] transition-colors duration-300">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <p className="text-[10px] text-stone-500 mt-1">
                Joined {new Date(user.joinDate).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2 mt-5">
            <div className="bg-[#E8DDCB] rounded-2xl p-2.5 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-sm font-bold text-stone-800">{streak.current}</span>
              </div>
              <p className="text-[9px] text-stone-500 leading-tight">Day Streak</p>
            </div>
            <div className="bg-[#E8DDCB] rounded-2xl p-2.5 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Award className="w-4 h-4 text-[#AA8347]" />
                <span className="text-sm font-bold text-stone-800">{earnedBadgeCount}</span>
              </div>
              <p className="text-[9px] text-stone-500 leading-tight">Badges</p>
            </div>
            <div className="bg-[#E8DDCB] rounded-2xl p-2.5 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <BookOpen className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-bold text-stone-800">{readPosts.length}</span>
              </div>
              <p className="text-[9px] text-stone-500 leading-tight">Read</p>
            </div>
            <div className="bg-[#E8DDCB] rounded-2xl p-2.5 text-center" data-testid="profile-mindful-stat">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Clock className="w-4 h-4 text-sky-500" />
                <span className="text-sm font-bold text-stone-800">{formatMindfulTime(mindfulSeconds)}</span>
              </div>
              <p className="text-[9px] text-stone-500 leading-tight">Mindful</p>
            </div>
          </div>
        </div>
      </div>

      {/* Talk to your Reflection Coach — re-entry from Profile since it was
          removed from Home in the 4-tab redesign. Opens the same overlay
          used previously so nothing about the coach flow changed. */}
      <div className="px-5 mb-6">
        <button
          type="button"
          onClick={() => setCoachOpen(true)}
          data-testid="profile-coach-btn"
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-[#FFFCF3] via-[#F7F3EA] to-[#FFFCF3] border border-[#D6AD60]/40 hover:border-[#D6AD60] hover:shadow-md transition-all duration-300 text-left group"
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
        >
          <div className="relative w-11 h-11 flex-shrink-0 flex items-center justify-center">
            <div
              className="absolute inset-[-4px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(214,173,96,0.45) 0%, transparent 65%)' }}
              aria-hidden
            />
            <div className="absolute inset-0 rounded-full border border-[#D6AD60]/50" aria-hidden />
            <div className="absolute inset-1.5 rounded-full border border-[#D6AD60]/35" aria-hidden />
            <div
              className="relative w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle at 32% 30%, #E8CE99 0%, #D6AD60 55%, #AA8347 100%)',
                boxShadow: '0 4px 12px -2px rgba(214,173,96,0.55), inset 0 -2px 5px rgba(0,0,0,0.08)',
              }}
            >
              <MessageCircle className="w-3 h-3 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#173F35]">Talk to your Reflection Coach</p>
            <p className="text-[11px] text-[#5B7A65]">Share what you&rsquo;re feeling · get an affirmation, breath, and meditation</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#AA8347] group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Streak Calendar */}
      <div className="px-5 mb-6">
        <div className="bg-[#FFFCF3] border border-stone-200 rounded-3xl p-5 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                Activity Streak
              </h3>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-stone-500">Best: {streak.longest} days</span>
            </div>
          </div>
          <div className="grid grid-cols-10 gap-1.5">
            {calendarDays.map((day) => (
              <div
                key={day.date}
                className={`aspect-square rounded-lg flex items-center justify-center text-[9px] font-medium transition-all duration-300 ${
                  day.active
                    ? 'bg-orange-400 text-white shadow-sm'
                    : day.isToday
                    ? 'bg-[#E8DDCB] text-[#D6AD60] ring-1 ring-[#D6AD60]/40'
                    : 'bg-[#E8DDCB] text-stone-300'
                }`}
                title={day.date}
              >
                {day.dayNum}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-[10px] text-stone-500">30 days ago</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded bg-[#E8DDCB]" />
                <span className="text-[10px] text-stone-500">Missed</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded bg-orange-400" />
                <span className="text-[10px] text-stone-500">Active</span>
              </div>
            </div>
            <span className="text-[10px] text-stone-500">Today</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 mb-4">
        <div className="flex bg-[#FFFCF3] border border-stone-200 rounded-2xl p-1 shadow-md">
          {[
            { id: 'badges', label: 'Badges', icon: Trophy },
            { id: 'liked', label: 'Liked', icon: Heart },
            { id: 'saved', label: 'Saved', icon: Bookmark },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-br from-[#D6AD60] to-[#D6AD60] text-[#1a1814] shadow-md'
                  : 'text-stone-500 hover:text-[#D6AD60]'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-5">
        {/* Badges Tab */}
        {activeTab === 'badges' && (
          <div className="animate-fade-in">
            {/* Progress */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-[#D6AD60]">
                {earnedBadgeCount} of {totalBadgeCount} earned
              </span>
              <div className="flex-1 mx-3 h-1.5 bg-[#E8DDCB] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#D6AD60] to-[#D6AD60] rounded-full transition-all duration-500"
                  style={{ width: `${(earnedBadgeCount / totalBadgeCount) * 100}%` }}
                />
              </div>
            </div>

            {/* Earned */}
            {earnedBadges.length > 0 && (
              <div className="mb-5">
                <h4 className="text-xs font-semibold text-[#D6AD60] uppercase tracking-wider mb-3">Earned</h4>
                <div className="grid grid-cols-3 gap-3">
                  {earnedBadges.map((badge) => {
                    const Icon = iconMap[badge.icon] || Trophy;
                    return (
                      <div
                        key={badge.id}
                        className="bg-[#FFFCF3] border border-[#D6AD60]/25 rounded-2xl p-3 shadow-md text-center animate-scale-in"
                      >
                        <div className="w-11 h-11 rounded-xl bg-[#E8DDCB] flex items-center justify-center mx-auto mb-2">
                          <Icon className="w-5 h-5 text-[#AA8347]" />
                        </div>
                        <p className="text-[11px] font-semibold text-stone-800 leading-tight">{badge.name}</p>
                        <p className="text-[9px] text-stone-500 mt-0.5 leading-tight">{badge.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked */}
            <div>
              <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Locked</h4>
              <div className="grid grid-cols-3 gap-3">
                {lockedBadges.map((badge) => {
                  const Icon = iconMap[badge.icon] || Trophy;
                  return (
                    <div
                      key={badge.id}
                      className="bg-[#FFFCF3]/60 border border-stone-100 rounded-2xl p-3 text-center opacity-60"
                    >
                      <div className="w-11 h-11 rounded-xl bg-[#E8DDCB] flex items-center justify-center mx-auto mb-2">
                        <Icon className="w-5 h-5 text-stone-300" />
                      </div>
                      <p className="text-[11px] font-semibold text-stone-500 leading-tight">{badge.name}</p>
                      <p className="text-[9px] text-stone-400 mt-0.5 leading-tight">{badge.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Liked Tab */}
        {activeTab === 'liked' && (
          <div className="animate-fade-in">
            {likedPosts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {likedPosts.map((postId) => {
                  const post = getPostById(postId);
                  if (!post) return null;
                  return (
                    <Link
                      key={postId}
                      to={`/post/${postId}`}
                      className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300 group"
                    >
                      <img src={post.image} alt={post.title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</p>
                        <p className="text-[10px] text-stone-500 mt-0.5 capitalize">{post.category.replace('-', ' ')}</p>
                      </div>
                      <Heart className="w-4 h-4 text-red-400 fill-red-400 flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Heart className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No liked posts yet</p>
                <p className="text-xs text-stone-400 mt-1">Tap the heart on posts you love</p>
              </div>
            )}
          </div>
        )}

        {/* Saved Tab */}
        {activeTab === 'saved' && (
          <div className="animate-fade-in">
            {bookmarkedPosts.length > 0 ? (
              <div className="flex flex-col gap-3">
                {bookmarkedPosts.map((postId) => {
                  const post = getPostById(postId);
                  if (!post) return null;
                  return (
                    <Link
                      key={postId}
                      to={`/post/${postId}`}
                      className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300 group"
                    >
                      <img src={post.image} alt={post.title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</p>
                        <p className="text-[10px] text-stone-500 mt-0.5 capitalize">{post.category.replace('-', ' ')}</p>
                      </div>
                      <Bookmark className="w-4 h-4 text-[#D6AD60] fill-[#D6AD60] flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bookmark className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Save the reflections that speak to you.
                </p>
                <p className="text-xs text-stone-400 mt-1">Tap the bookmark on anything and it lands here.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* In-progress Programs */}
      {inProgress.length > 0 && (
        <div className="px-5 mb-6" data-testid="profile-in-progress-programs">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-[#D6AD60]" />
            <h3 className="text-xs font-semibold text-[#D6AD60] uppercase tracking-wider">In progress</h3>
          </div>
          <div className="flex flex-col gap-2">
            {inProgress.map((row) => {
              const program = getProgramById(row.programId);
              if (!program) return null;
              const done = (row.completed || []).length;
              const pct = Math.round((done / program.sessions.length) * 100);
              return (
                <Link
                  key={row.programId}
                  to={`/programs/${row.programId}`}
                  className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3 shadow-sm hover:shadow-md hover:border-[#D6AD60]/30 transition-all"
                  data-testid={`profile-program-${row.programId}`}
                >
                  <img src={program.image} alt={program.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-800 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {program.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#D6AD60] to-[#D6AD60]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-stone-500">{done}/{program.sessions.length}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-400" />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Journal Section */}
      <PushOptInCard />
      <div className="px-5 mb-6">
        <Link
          to="/journal"
          className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-4 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300 group"
          data-testid="profile-journal-link"
        >
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#D6AD60]/30 to-[#D6AD60]/20 flex items-center justify-center flex-shrink-0">
            <BookHeart className="w-5 h-5 text-[#AA8347]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              My Journal
            </h3>
            <p className="text-[11px] text-stone-500 flex items-center gap-1 mt-0.5">
              <Lock className="w-3 h-3" /> Private — only you can see this
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-500 group-hover:text-[#D6AD60] transition-colors flex-shrink-0" />
        </Link>
      </div>

      {/* Friends Section */}
      <div className="px-5 mb-6">
        <FriendsSection user={user} onUpdateUser={() => { refreshProfile(); }} />
      </div>

      {/* Push Notifications */}
      <div className="px-5 mb-6">
        <ReflectionReminderSettings />
        <MySharedLinks />
        <div className="my-4 h-px bg-stone-200" />
        <PushNotificationSettings />
      </div>

      {/* Moderator tools — admin only */}
      {user?.isAdmin && <ModeratorPanel />}

      {/* Milestones — supportive progress, no leaderboards */}
      <MilestoneBadges />

      {/* Danger zone — permanent account deletion */}
      <DangerZone />


      {/* Logout Confirmation */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-[#FFFCF3] border border-[#D6AD60]/25 rounded-3xl p-6 mx-5 w-full max-w-[320px] shadow-2xl animate-scale-in">
            <h3 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Log out?
            </h3>
            <p className="text-sm text-stone-500 mb-5">
              Your streaks and badges will be reset.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2.5 bg-[#E8DDCB] text-stone-800 rounded-xl text-sm font-medium hover:bg-[#DBC9A3] transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-2.5 bg-red-500/90 text-white rounded-xl text-sm font-medium hover:bg-red-500 transition-colors duration-300"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}

      <ReflectionCoachOverlay open={coachOpen} onClose={() => setCoachOpen(false)} />
    </div>
  );
};

export default ProfilePage;
